/**
 * CR306+CR311: Actions section — page actions (click, scroll, wait, write, press) before scraping
 */

import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Zap, Save, Plus, Trash2 } from "lucide-react";
import type { Recipe } from "@/lib/recipes-studio/types";
import type { FirecrawlRecipeConfig, RecipeAction, RecipeActionType } from "@/lib/recipes-studio/config-types";

interface RecipeActionsSectionProps {
  recipe: Recipe;
  saving: boolean;
  onSave: (updates: Partial<Recipe>) => void;
}

const ACTION_TYPES: { value: RecipeActionType; label: string }[] = [
  { value: "click", label: "Click" },
  { value: "write", label: "Write" },
  { value: "press", label: "Press" },
  { value: "scroll", label: "Scroll" },
  { value: "wait", label: "Wait" },
  { value: "screenshot", label: "Screenshot" },
];

const PRESS_KEYS = ["Enter", "Tab", "Escape"];

function emptyAction(): RecipeAction {
  return { type: "click", selector: "", wait_ms: undefined };
}

export function RecipeActionsSection({ recipe, saving, onSave }: RecipeActionsSectionProps) {
  const cfg = (recipe.recipe_config ?? {}) as FirecrawlRecipeConfig;
  const [actions, setActions] = useState<RecipeAction[]>(cfg.actions ?? []);

  useEffect(() => {
    const c = (recipe.recipe_config ?? {}) as FirecrawlRecipeConfig;
    setActions(c.actions ?? []);
  }, [recipe.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const origJson = JSON.stringify(cfg.actions ?? []);
  const hasChanges = JSON.stringify(actions) !== origJson;

  const handleSave = useCallback(() => {
    // Strip empty actions
    const cleaned = actions.filter(
      (a) =>
        a.type === "wait" ||
        a.type === "screenshot" ||
        a.type === "press" ||
        a.selector?.trim() ||
        a.text?.trim()
    );
    const updated: FirecrawlRecipeConfig = { ...cfg, actions: cleaned };
    onSave({ recipe_config: updated as Record<string, unknown> });
  }, [cfg, actions, onSave]);

  const updateAction = (idx: number, patch: Partial<RecipeAction>) => {
    setActions((prev) => prev.map((a, i) => (i === idx ? { ...a, ...patch } : a)));
  };

  const removeAction = (idx: number) => {
    setActions((prev) => prev.filter((_, i) => i !== idx));
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Zap className="h-4 w-4 text-muted-foreground" />
            Actions
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => setActions((prev) => [...prev, emptyAction()])}
          >
            <Plus className="h-3 w-3 mr-1" />
            Add
          </Button>
        </div>
        <p className="text-[10px] text-muted-foreground mt-1">
          Use tokens like <code className="bg-muted px-1 rounded">{"{{postal_code}}"}</code> in Write actions.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {actions.length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-4">
            No actions. Firecrawl will scrape the page as-is.
          </p>
        )}

        {actions.map((action, idx) => (
          <div key={idx} className="flex items-start gap-2 p-2 border rounded-md bg-muted/30">
            {/* Type */}
            <div className="flex-shrink-0 w-24">
              <Label className="text-[10px] text-muted-foreground">Type</Label>
              <Select
                value={action.type}
                onValueChange={(v) => updateAction(idx, { type: v as RecipeActionType })}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-popover border shadow-lg z-50">
                  {ACTION_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Selector — for click, scroll, write */}
            {(action.type === "click" || action.type === "scroll" || action.type === "write") && (
              <div className="flex-1">
                <Label className="text-[10px] text-muted-foreground">Selector</Label>
                <Input
                  value={action.selector ?? ""}
                  onChange={(e) => updateAction(idx, { selector: e.target.value })}
                  placeholder={action.type === "write" ? "#postal-code-input" : "#deals-tab"}
                  className="h-8 text-xs font-mono"
                />
              </div>
            )}

            {/* Text — for write */}
            {action.type === "write" && (
              <div className="flex-1">
                <Label className="text-[10px] text-muted-foreground">Text</Label>
                <Input
                  value={action.text ?? ""}
                  onChange={(e) => updateAction(idx, { text: e.target.value })}
                  placeholder="{{postal_code}}"
                  className="h-8 text-xs font-mono"
                />
              </div>
            )}

            {/* Key — for press */}
            {action.type === "press" && (
              <div className="w-28">
                <Label className="text-[10px] text-muted-foreground">Key</Label>
                <Select
                  value={action.key ?? "Enter"}
                  onValueChange={(v) => updateAction(idx, { key: v })}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border shadow-lg z-50">
                    {PRESS_KEYS.map((k) => (
                      <SelectItem key={k} value={k}>
                        {k}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Wait ms — for wait, click, write, press */}
            {(action.type === "wait" || action.type === "click" || action.type === "write" || action.type === "press") && (
              <div className="w-20">
                <Label className="text-[10px] text-muted-foreground">Wait ms</Label>
                <Input
                  type="number"
                  min={0}
                  value={action.wait_ms ?? ""}
                  onChange={(e) =>
                    updateAction(idx, {
                      wait_ms: e.target.value ? Number(e.target.value) : undefined,
                    })
                  }
                  placeholder="1000"
                  className="h-8 text-xs"
                />
              </div>
            )}

            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 mt-4 text-destructive hover:text-destructive"
              onClick={() => removeAction(idx)}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}

        {hasChanges && (
          <Button size="sm" onClick={handleSave} disabled={saving}>
            <Save className="h-3.5 w-3.5 mr-1.5" />
            {saving ? "Saving…" : "Save"}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
